import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import PageShell from "@/components/site/PageShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CountryDetail() {
  const { slug } = useParams();
  const { i18n, t } = useTranslation();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/countries/${slug}`, { params: { lang: i18n.language } });
        if (!cancelled) setC(data);
      } catch (e) {
        if (!cancelled) setC(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, i18n.language]);

  if (loading) {
    return <PageShell><div className="max-w-3xl mx-auto px-6 md:px-10 py-32 text-center text-[#475569]">Caricamento…</div></PageShell>;
  }
  if (!c) {
    return (
      <PageShell>
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-32 text-center">
          <h1 className="font-display text-4xl text-[#1a365d]">404</h1>
          <p className="mt-4 text-[#475569]">Paese non trovato.</p>
          <Link to="/" className="btn-outline mt-8"><ArrowLeft size={14} /> Home</Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* Hero with flag */}
      <section className="bg-[#fafaf7] border-b border-[#e2e8f0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-16">
          <Link to="/" className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.28em] uppercase text-[#475569] hover:text-[#1a365d]">
            <ArrowLeft size={12} /> EU 27
          </Link>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-5 reveal">
              <img
                src={`https://flagcdn.com/w1280/${c.code}.png`}
                srcSet={`https://flagcdn.com/w640/${c.code}.png 640w, https://flagcdn.com/w1280/${c.code}.png 1280w`}
                sizes="(max-width: 1024px) 100vw, 40vw"
                alt={`${c.name} flag`}
                data-testid={`country-hero-flag-${c.code}`}
                className="w-full max-w-md border border-[#e2e8f0] shadow-lg aspect-[3/2] object-cover"
              />
            </div>
            <div className="lg:col-span-7 reveal delay-1">
              <span className="eyebrow">— EU 27</span>
              <h1 className="font-display text-5xl md:text-7xl text-[#1a365d] mt-4 leading-[1.02] tracking-tight uppercase">
                {c.name}
              </h1>
              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-[#e2e8f0] pt-6">
                <div>
                  <div className="text-[10.5px] tracking-[0.28em] uppercase text-[#475569]">{t("country.capital")}</div>
                  <div className="font-display text-lg md:text-xl text-[#1a365d] mt-1.5 leading-tight">{c.capital}</div>
                </div>
                <div>
                  <div className="text-[10.5px] tracking-[0.28em] uppercase text-[#475569]">{t("country.euMember")}</div>
                  <div className="font-display text-lg md:text-xl text-[#1a365d] mt-1.5 leading-tight">{c.eu_year}</div>
                </div>
                <div>
                  <div className="text-[10.5px] tracking-[0.28em] uppercase text-[#475569]">{t("country.population")}</div>
                  <div className="font-display text-lg md:text-xl text-[#1a365d] mt-1.5 leading-tight">{c.population}</div>
                </div>
                <div>
                  <div className="text-[10.5px] tracking-[0.28em] uppercase text-[#475569]">{t("country.language")}</div>
                  <div className="font-display text-lg md:text-xl text-[#1a365d] mt-1.5 leading-tight">{c.language}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <p className="font-display text-2xl md:text-3xl text-[#1a365d] leading-[1.35] reveal">
            {c.intro}
          </p>
        </div>
      </section>

      {/* Traditions */}
      <section className="bg-[#fafaf7] py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 reveal">
            <span className="eyebrow">— {t("country.identity")}</span>
            <h2 className="font-display text-3xl md:text-5xl text-[#1a365d] mt-4 leading-[1.08]">{t("country.culturalTraditions")}.</h2>
          </div>
          <div className="lg:col-span-7 reveal delay-1">
            <p className="text-[#475569] leading-[1.85] text-base md:text-[17px]">{c.traditions}</p>
          </div>
        </div>
      </section>

      {/* Festivals */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 reveal">
            <span className="eyebrow">— {t("country.internationalEvents")}</span>
            <h2 className="font-display text-3xl md:text-5xl text-[#1a365d] mt-4 leading-[1.08]">{t("country.internationalEvents")}.</h2>
          </div>
          <div className="lg:col-span-7 reveal delay-1">
            <p className="text-[#475569] leading-[1.85] text-base md:text-[17px]">{c.festivals}</p>
          </div>
        </div>
      </section>

      {/* Cities */}
      <section className="bg-[#fafaf7] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-12 reveal">
            <span className="eyebrow">— {t("country.keyCities")}</span>
            <h2 className="font-display text-3xl md:text-5xl text-[#1a365d] mt-4 leading-[1.08]">{t("country.cities")} <span className="italic">{c.name}</span>.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {c.cities.map((city, i) => (
              <div key={city.name} className={`bg-white border-l-2 border-[#d4af37] p-7 md:p-8 reveal delay-${(i % 4) + 1}`}>
                <span className="text-[10.5px] tracking-[0.28em] uppercase text-[#d4af37]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display text-2xl text-[#1a365d] mt-3 leading-tight">{city.name}</h3>
                <p className="font-body text-[#475569] text-[14.5px] leading-[1.8] mt-3">{city.desc || city.blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tesori 2026 teaser */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center reveal">
          <span className="eyebrow">— Tesori 2026</span>
          <h2 className="font-display text-3xl md:text-5xl text-[#1a365d] mt-4 leading-[1.08]">{t("country.treasuresTeaser")} <span className="italic">{c.name}</span>.</h2>
          <p className="mt-6 text-[#475569] leading-[1.85] text-base md:text-[17px]">
            {t("country.treasuresIntro")}
          </p>
          <Link to="/news-archive" className="btn-outline mt-10">{t("country.latestNews")}</Link>
        </div>
      </section>
    </PageShell>
  );
}
